// The Inspector's rows for a cortical region in Drugs › Measured ΔFC (features/DfcInspector.tsx): which Schaefer-400
// parcels lie inside the picked DKT region on the HD cortex the viewport draws, by their share of its vertices, each
// with its change in the condition, and the region's change as their vertex-weighted mean. The shares come from the
// pack's own label layers through the crosswalk pharma's region overlay already uses (data/sims/crosswalk.ts), and
// the mean is what `remap` gives a region, so the rows agree with a region colouring of the same map.

import type { Crosswalk } from '../data/sims/crosswalk.ts';
import type { DfcNode } from './dfc.ts';

export interface RegionParcel {
  node: DfcNode;
  /** Its vertices inside the region, and their share of the region's vertices. */
  vertices: number;
  share: number;
  /** Its change in the condition, Δz; null where the export has no measurement for it. */
  dz: number | null;
}

export interface RegionDfc {
  /** Parcels with at least `minShare` of the region's vertices, largest share first. */
  parcels: RegionParcel[];
  /** Parcels under that share (slivers along the region's border): counted, not listed. */
  slivers: number;
  /** The region's vertices that carry a parcel the export has. */
  vertices: number;
  /** Its measured parcels (slivers included) and the vertex-weighted mean of their changes; null with none. */
  measured: number;
  mean: number | null;
}

/**
 * A DKT region's Schaefer parcels and change, from the crosswalk `cw` (schaefer400 → dkt), the export's nodes, a
 * condition's strength by node index and its measured test. Null for a structure the crosswalk hasn't (nothing
 * cortical under that id).
 */
export function regionDfc(cw: Crosswalk, structure: string, nodes: readonly DfcNode[], strength: ArrayLike<number>, measured: (index: number) => boolean, minShare = 0.01): RegionDfc | null {
  const k = cw.keys.indexOf(structure);
  if (k < 0) return null;
  const parts = cw.parts[k] ?? [];
  const byLabel = new Map(nodes.map((n) => [n.label, n]));
  const total = parts.reduce((a, p) => (byLabel.has(p.label) ? a + p.vertices : a), 0);
  if (!total) return null;
  const parcels: RegionParcel[] = [];
  let slivers = 0;
  let n = 0;
  let sum = 0;
  let weight = 0;
  for (const p of parts) {
    const node = byLabel.get(p.label);
    if (!node) continue;
    const dz = measured(node.index) ? (strength[node.index] ?? NaN) : null;
    if (dz !== null && Number.isFinite(dz)) {
      n++;
      sum += dz * p.vertices;
      weight += p.vertices;
    }
    const share = p.vertices / total;
    if (share < minShare) {
      slivers++;
      continue;
    }
    parcels.push({ node, vertices: p.vertices, share, dz });
  }
  parcels.sort((a, b) => b.share - a.share || a.node.index - b.node.index);
  return { parcels, slivers, vertices: total, measured: n, mean: weight > 0 ? sum / weight : null };
}

/** A share as people read it: whole percent from 10 % up, one decimal under. */
export const shareText = (share: number): string => `${share >= 0.095 ? Math.round(share * 100) : (share * 100).toFixed(1)} %`;
